import { useEffect, useState } from "react";
import { DuelGame } from "./components/DuelGame";
import { Game } from "./components/Game";
import { KumonApp } from "./components/KumonApp";
import { Menu } from "./components/Menu";
import { Results } from "./components/Results";
import { generateQuestionSet } from "./game/questions";
import { saveDuelRecord } from "./game/storage";
import {
  DUEL_QUESTION_COUNT,
  SOLO_QUESTION_COUNT,
  winnerOf,
  type GameConfig,
  type Question,
  type RoundResult,
} from "./game/types";
import "./index.css";

type Screen =
  | { kind: "menu" }
  | { kind: "game"; config: GameConfig; questions: Question[] }
  | { kind: "duel-game"; config: GameConfig; questions: Question[] }
  | { kind: "results"; config: GameConfig; p1: RoundResult; p2?: RoundResult };

export function App() {
  const [route, setRoute] = useState<"mathwar" | "kumon">(() =>
    typeof window !== "undefined" && window.location.pathname.startsWith("/kumon")
      ? "kumon"
      : "mathwar",
  );

  const [screen, setScreen] = useState<Screen>({ kind: "menu" });

  useEffect(() => {
    const handlePopState = () => {
      const isKumon = window.location.pathname.startsWith("/kumon");
      setRoute(isKumon ? "kumon" : "mathwar");
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigateToKumon = () => {
    window.history.pushState({}, "", "/kumon");
    setRoute("kumon");
  };

  const navigateToClassic = () => {
    window.history.pushState({}, "", "/");
    setRoute("mathwar");
  };

  if (route === "kumon") {
    return <KumonApp onNavigateToClassic={navigateToClassic} />;
  }

  const start = (config: GameConfig) => {
    if (config.mode === "duel") {
      setScreen({
        kind: "duel-game",
        config,
        questions: generateQuestionSet(config.selectedCategory, DUEL_QUESTION_COUNT),
      });
    } else {
      setScreen({
        kind: "game",
        config,
        questions: generateQuestionSet(config.selectedCategory, SOLO_QUESTION_COUNT),
      });
    }
  };

  const handleSoloFinish = (result: RoundResult) => {
    if (screen.kind !== "game") return;
    setScreen({ kind: "results", config: screen.config, p1: result });
  };

  const handleDuelFinish = (p1: RoundResult, p2: RoundResult) => {
    if (screen.kind !== "duel-game") return;
    saveDuelRecord({
      winner: winnerOf(p1, p2),
      p1,
      p2,
      date: new Date().toISOString(),
    }).catch(() => {});
    setScreen({ kind: "results", config: screen.config, p1, p2 });
  };

  const rematch = () => {
    if (screen.kind !== "results") return;
    start(screen.config);
  };

  const isGame = screen.kind === "game" || screen.kind === "duel-game";
  const isDuel = screen.kind === "duel-game";

  return (
    <div className={`app ${isGame ? "app-game" : ""} ${isDuel ? "app-duel" : ""}`}>
      {screen.kind === "menu" && (
        <Menu onStart={start} onNavigateToKumon={navigateToKumon} />
      )}

      {screen.kind === "game" && (
        <Game
          key={screen.config.player1Name}
          name={screen.config.player1Name}
          questions={screen.questions}
          onFinish={handleSoloFinish}
        />
      )}

      {screen.kind === "duel-game" && (
        <DuelGame
          key={screen.config.player1Name}
          player1Name={screen.config.player1Name}
          player2Name={screen.config.player2Name}
          questions={screen.questions}
          onFinish={handleDuelFinish}
        />
      )}

      {screen.kind === "results" && (
        <Results
          config={screen.config}
          p1={screen.p1}
          p2={screen.p2}
          onRematch={rematch}
          onMenu={() => setScreen({ kind: "menu" })}
        />
      )}
    </div>
  );
}

export default App;
